import React, {FC} from 'react';
import {FieldClassName} from '../_internal/Field/Field.classes';
import {FieldLayout} from '../_internal/Field/FieldLayout';
import {FieldLabel, FieldLabelWithInfo} from '../_internal/FieldLabel/FieldLabel';
import {FieldLabelProps} from '../_internal/FieldLabel/FieldLabel.types';
import {RsnSkeleton} from '../SkeletalElements/Skeleton';
import {InputPlugin, RsnInputPropsWithPlugin, usePassword, useRsnInputPlugin, useSearch} from './RsnInput.hooks';
import {RsnInputExtensions, RsnInputProps} from './RsnInput.types';

function RsnInput({label, decoration, helperText, isInvalid = false, inputRef, issue, ...attrs}: RsnInputProps) {
    if (typeof label === 'string') {
        label = <RsnInput.Label>{label}</RsnInput.Label>;
    }
    return (
        <FieldLayout
            decoration={decoration}
            label={label}
            issue={issue}
            helperText={helperText}
            isInvalid={isInvalid}
            isDisabled={attrs.disabled}
        >
            <input {...attrs} ref={inputRef} className={FieldClassName()} />
        </FieldLayout>
    );
}

namespace RsnInput {
    export type Props = RsnInputProps;
    export type LabelProps = FieldLabelProps;
    export type Plugin<T> = InputPlugin<T>;
}

const Password = (props: Omit<RsnInputProps, 'type'>) => {
    return <RsnInput {...usePassword(props)} />;
};
Password.displayName = 'RsnInput.Password';

const Search = (props: Omit<RsnInputProps, 'type'>) => {
    return <RsnInput {...useSearch(props)} />;
};
Search.displayName = 'RsnInput.Search';

const Plain: FC<Omit<RsnInputProps, keyof RsnInputExtensions>> = ({
    label: _label,
    helperText: _helperText,
    ...props
}: RsnInputProps) => <RsnInput {...props} />;
Plain.displayName = 'RsnInput.Plain';

const Extended = <T,>(props: RsnInputPropsWithPlugin<T>) => {
    return <RsnInput {...useRsnInputPlugin(props)} />;
};
Extended.displayName = 'RsnInput.Extended';

const Skeleton = React.memo(() => (
    <div className='grid gap-2'>
        <RsnSkeleton width={Math.random() * 100 + 40} height={12} />
        <div className='flex gap-2 rounded-sm border border-solid border-neutral-40 px-3 py-4'>
            <RsnSkeleton width={Math.random() * 80 + 100} height={12} />
        </div>
    </div>
));
Skeleton.displayName = 'RsnInput.Skeleton';

RsnInput.Label = FieldLabel.bind(null);
RsnInput.Label.displayName = 'RsnInput.Label';
RsnInput.LabelWithInfo = FieldLabelWithInfo.bind(null);
RsnInput.LabelWithInfo.displayName = 'RsnInput.LabelWithInfo';
RsnInput.Password = Password;
RsnInput.Search = Search;
RsnInput.Plain = Plain;
RsnInput.Extended = Extended;
RsnInput.Skeleton = Skeleton;

export default RsnInput;
